import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/api';
import type { NoteListResponse } from '@/api/api';
import { surfaceApiError } from '@/lib/api-error';

export const noteKeys = {
  all: ['notes'] as const,
  byProject: (projectId: string) => [...noteKeys.all, 'project', projectId] as const,
  detail: (noteId: string) => [...noteKeys.all, 'detail', noteId] as const,
  latestVersion: (noteId: string) => [...noteKeys.detail(noteId), 'latest-version'] as const,
};

type ReorderVars = { parentId: string | null; orderedIds: string[] };

/**
 * Sibling reorder mutation. Optimistically rewrites the cached `NoteListResponse[]`
 * so the new order shows instantly, then reconciles with the server on settle.
 * `orderedIds` is the full ordered id list for one sibling group; the server
 * assigns `position = index`.
 */
export function useReorderNotesMutation(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ parentId, orderedIds }: ReorderVars) =>
      api.reorderNotes(projectId, { parent_id: parentId, ordered_ids: orderedIds }),
    onMutate: async ({ orderedIds }) => {
      const key = noteKeys.byProject(projectId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<NoteListResponse[]>(key);
      if (previous) {
        // Reassign `position` within the reordered group; other groups untouched.
        const posById = new Map(orderedIds.map((id, i) => [id, i]));
        qc.setQueryData<NoteListResponse[]>(
          key,
          previous.map((n) => (posById.has(n.id) ? { ...n, position: posById.get(n.id)! } : n)),
        );
      }
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(noteKeys.byProject(projectId), ctx.previous);
      surfaceApiError(err);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: noteKeys.byProject(projectId) });
    },
  });
}

export const notesListOptions = (projectId: string) =>
  queryOptions({
    queryKey: noteKeys.byProject(projectId),
    queryFn: () => api.listNotes(projectId),
    // Every note mutation updates or invalidates this list, so it can stay fresh
    // for the session — returning to the tab needs no refetch.
    staleTime: 5 * 60_000,
  });

export const noteDetailOptions = (noteId: string) =>
  queryOptions({
    queryKey: noteKeys.detail(noteId),
    queryFn: () => api.getNote(noteId),
    staleTime: 30_000,
  });

export const noteLatestVersionOptions = (noteId: string) =>
  queryOptions({
    queryKey: noteKeys.latestVersion(noteId),
    queryFn: () => api.getLatestNoteVersion(noteId),
    staleTime: 30_000,
  });
